import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, Wrench, MessageSquare } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useData } from '../contexts/DataContext';
import { applianceTypes, complaintTypes } from '../mock';
import { useToast } from '../hooks/use-toast';

const MakeClaim = () => {
  const [params] = useSearchParams();
  const initialType = params.get('type') === 'complaint' || params.get('type') === 'Complaint' ? 'Complaint' : 'Repair';
  const [type, setType] = useState(initialType);
  const [appliance, setAppliance] = useState(params.get('appliance') || '');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState(params.get('description') || '');
  const [urgency, setUrgency] = useState(params.get('urgency') || 'normal');
  const { addClaim } = useData();
  const nav = useNavigate();
  const { toast } = useToast();

  const submit = (e) => {
    e.preventDefault();
    const applianceLabel = type === 'Repair' ? appliance : (category || 'Customer service');
    if (!applianceLabel || description.length < 10) {
      return toast({ title: 'Please add more detail', description: 'Give us at least a short description so we can help.' });
    }
    const claim = addClaim({ type, appliance: applianceLabel, description, urgency });
    toast({ title: `${type} submitted`, description: `Reference: ${claim.id}` });
    nav(`/dashboard/claim/${claim.id}`);
  };

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-5">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F0068]/70 hover:text-[#1F0068] mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F0068] tracking-tight">New claim or complaint</h1>
          <p className="mt-2 text-[#1F0068]/70">Tell us what’s happened and we’ll take it from there.</p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {[
              { id: 'Repair', label: 'Book a repair', icon: Wrench, tint: 'bg-[#FFEDD6]' },
              { id: 'Complaint', label: 'Make a complaint', icon: MessageSquare, tint: 'bg-[#FCE0EC]' },
            ].map((t) => {
              const Ico = t.icon; const sel = type === t.id;
              return (
                <button key={t.id} onClick={() => setType(t.id)}
                  className={`p-5 rounded-2xl border-2 text-left ${sel ? 'border-[#1F0068] bg-white' : 'border-transparent bg-white/60 hover:bg-white'}`}>
                  <div className={`w-11 h-11 rounded-xl ${t.tint} flex items-center justify-center mb-2 text-[#1F0068]`}>
                    <Ico className="w-5 h-5" />
                  </div>
                  <div className="font-bold text-[#1F0068]">{t.label}</div>
                  <div className="text-xs text-[#1F0068]/60 mt-0.5">{t.id === 'Repair' ? 'Get an engineer booked' : 'Log an issue for review'}</div>
                </button>
              );
            })}
          </div>

          <form onSubmit={submit} className="mt-6 bg-white rounded-3xl p-6 md:p-8 border border-[#1F0068]/5 space-y-5">
            {type === 'Repair' ? (
              <div>
                <Label className="text-[#1F0068] font-semibold">Which appliance?</Label>
                <Select value={appliance} onValueChange={setAppliance}>
                  <SelectTrigger className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"><SelectValue placeholder="Choose an appliance" /></SelectTrigger>
                  <SelectContent>
                    {applianceTypes.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-[#1F0068] font-semibold">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                  <SelectContent>
                    {complaintTypes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-[#1F0068] font-semibold">Describe the issue</Label>
              <Textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Please include when it started, any error codes, and what you’ve already tried."
                className="mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 text-[#1F0068]" />
            </div>

            <div>
              <Label className="text-[#1F0068] font-semibold">How urgent is it?</Label>
              <RadioGroup value={urgency} onValueChange={setUrgency} className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { id: 'low', label: 'Not urgent' },
                  { id: 'normal', label: 'Normal' },
                  { id: 'high', label: 'Emergency' },
                ].map((u) => (
                  <label key={u.id} className={`flex items-center justify-center h-12 rounded-xl border-2 cursor-pointer font-semibold ${urgency === u.id ? 'border-[#1F0068] bg-[#FFEDD6] text-[#1F0068]' : 'border-transparent bg-[#FFF6EB] text-[#1F0068]/70'}`}>
                    <RadioGroupItem value={u.id} className="sr-only" />
                    {u.label}
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="pt-4 border-t border-[#1F0068]/5 flex items-center justify-between">
              <div className="text-xs text-[#1F0068]/60">You’ll get a reference number after submitting.</div>
              <Button type="submit" className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white h-12 px-7 gap-1.5 font-semibold">
                Submit <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MakeClaim;
